import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { hivesApi } from "../services/hivesApi";
import { healthApi } from "../services/healthApi";
import { useFeatureTheme } from "../context/FeatureThemeContext";
import { useToast } from "../context/ToastContext";
import { seasonDisputed } from "../components/hives/hiveUrgency";
import { Card } from "../components/common/Card";
import { Button } from "../components/common/Button";
import { Badge } from "../components/common/Badge";
import { Tabs } from "../components/common/Tabs";
import { Spinner } from "../components/common/Spinner";
import { QrCodeLabel } from "../components/hives/QrCodeLabel";
import { SiteLocationDialog } from "../components/hives/SiteLocationDialog";
import { PrintHiveReport } from "../components/print/PrintHiveReport";
import { LockedFeature } from "../components/common/LockedFeature";
import { VoiceNoteButton } from "../components/common/VoiceNoteButton";
import { Modal } from "../components/common/Modal";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import { SelectOrAdd } from "../components/common/SelectOrAdd";
import { Field } from "../components/common/Field";
import { OverviewTab } from "./hiveDetail/OverviewTab";
import { IssuePanel } from "../components/hives/IssuePanel";
import { LogActionDialog } from "../components/hives/LogActionDialog";
import { RecordLossDialog } from "../components/hives/RecordLossDialog";
import { useHelpDisclosure } from "../components/common/HelpDisclosure";
import { HiveChecklistCard } from "../components/hives/HiveChecklistCard";
import { formatDateOnly, ACTION_GROUPS } from "../services/hiveActions";
import { QueenTab } from "./hiveDetail/QueenTab";
import { TreatmentsTab } from "./hiveDetail/TreatmentsTab";
import { FeedingsTab } from "./hiveDetail/FeedingsTab";
import { HarvestTab } from "./hiveDetail/HarvestTab";
import { EquipmentTab } from "./hiveDetail/EquipmentTab";
import { BloomTab } from "./hiveDetail/BloomTab";
import { ColonyEventsTab } from "./hiveDetail/ColonyEventsTab";
import { HiveRowNav } from "../components/hives/HiveRowNav";



export function HiveDetailPage() {
  const { hiveId } = useParams();
  const navigate = useNavigate();
  const { hasFeature } = useFeatureTheme();
  const { notify } = useToast();

  const [full, setFull] = useState(null);
  const [healthScore, setHealthScore] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [withdrawal, setWithdrawal] = useState([]);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [siteLocationOpen, setSiteLocationOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [lossOpen, setLossOpen] = useState(false);
  /* AT THE TOP, NOT BESIDE THE CARD. The health card renders behind
     `hasFeature("HealthScoring") && healthScore`, and a hook called
     inside that would run on some renders and not others -- the rules of
     hooks violation that breaks quietly rather than loudly. */
  const scoreHelp = useHelpDisclosure("the health score");
  // #389. The hive closes for the season, not the account -- half an
  // operation goes to Florida while the rest keeps working.
  const [seasonOpen, setSeasonOpen] = useState(false);
  const [closedUntil, setClosedUntil] = useState("");
  const [savingSeason, setSavingSeason] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [pendingAction, setPendingAction] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await hivesApi.getFull(hiveId);
      setFull(data);
    } catch (err) {
      notify(err.apiMessage || "Could not load hive", "danger");
      navigate("/hives");
    }
    hivesApi.listPhotos(hiveId).then((r) => setPhotos(r.photos || [])).catch(() => {});
    /* Whether this hive's honey is still inside a treatment's withdrawal
       period (#404). Best-effort and alongside the photos rather than
       inside the try above: a failure here must not send somebody back to
       the hive list, and the dialog simply says nothing when it has
       nothing to say. */
    hivesApi.withdrawal()
      .then((r) => setWithdrawal(r.withdrawal || []))
      .catch(() => {});
  }, [hiveId, navigate, notify]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (hasFeature("HealthScoring")) {
      healthApi.getScore(hiveId).then(setHealthScore).catch(() => {});
    }
  }, [hiveId, hasFeature]);

  // Quick actions now open a confirmation step instead of firing straight
  // away, so a frame count can be captured (#185) and a revisit scheduled
  // (#170). It also fixes the older complaint that a single click gave no
  // feedback at all -- the action was saving, but nothing said so.
  async function submitAction({ details, followUp }) {
    try {
      await hivesApi.logAction(hiveId, { action_type: pendingAction.type, details });
      // The action is the important half. If the reminder fails, say so
      // rather than reporting a blanket failure for something that did save.
      let reminderFailed = false;
      if (followUp) {
        try {
          await hivesApi.createFollowUp(hiveId, followUp);
        } catch {
          reminderFailed = true;
        }
      }
      notify(
        reminderFailed
          ? `${pendingAction.label} logged, but the reminder could not be saved`
          : followUp
            ? `${pendingAction.label} logged, reminder set`
            : `${pendingAction.label} logged`,
        reminderFailed ? "warning" : "success",
      );
      setPendingAction(null);
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not log action", "danger");
    }
  }

  async function completeFollowUp(taskId) {
    try {
      await hivesApi.completeFollowUp(hiveId, taskId);
      notify("Reminder cleared", "success");
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not clear the reminder", "danger");
    }
  }

  const [restoring, setRestoring] = useState(false);

  async function setSeason(until) {
    setSavingSeason(true);
    try {
      await hivesApi.setSeason([hive.hive_id], until);
      // Reload rather than patch: closing changes the Next Check date and
      // the overdue marker on this very page, both computed server-side.
      // Patching the flag alone would leave a hive that says it is closed
      // beside a date that still says it is overdue.
      await load();
      setSeasonOpen(false);
      notify(
        until
          ? `Closed for the season until ${new Date(until).toLocaleDateString()}`
          : "Back on your ordinary schedule",
        "success",
      );
    } catch (err) {
      notify(err.apiMessage || "Could not change the season for this hive", "danger");
    } finally {
      setSavingSeason(false);
    }
  }

  async function restore() {
    setRestoring(true);
    try {
      await hivesApi.restore(hive.hive_id);
      // Reload rather than patch the local object: the status change
      // also brings back the actions, banners and buttons that were
      // hidden for an archived hive, and a half-updated page is how
      // somebody ends up looking at stale controls.
      await load();
      notify("Hive restored to your active list", "success");
    } catch (err) {
      notify(err.apiMessage || "Could not restore this hive", "danger");
    } finally {
      setRestoring(false);
    }
  }

  // Optional, and reset nowhere: the dialog closes by navigating away.
  const [archiveReason, setArchiveReason] = useState("");
  const [archiveNote, setArchiveNote] = useState("");

  async function archiveHive() {
    setArchiving(true);
    try {
      await hivesApi.remove(hiveId, { reason: archiveReason, note: archiveNote.trim() });
      notify(`Hive ${hive.hive_number} archived`, "success");
      // Back to the list: staying on the detail page of a hive that was just
      // retired leaves the user looking at a stale record with no next step.
      navigate("/hives");
    } catch (err) {
      notify(err.apiMessage || "Could not archive this hive", "danger");
      setArchiving(false);
      setArchiveOpen(false);
    }
  }

  async function submitNote() {
    if (!noteText.trim()) return notify("Note is empty", "warning");
    try {
      await hivesApi.logAction(hiveId, { action_type: "Inspection", details: { note: noteText.trim() } });
      notify("Note logged", "success");
      setNoteModalOpen(false);
      setNoteText("");
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not log note", "danger");
    }
  }

  if (!full) return <Spinner label="Loading hive" />;
  const { hive, queen, health_scores: healthScores, actions, treatments, feedings, follow_ups: followUps } = full;

  const tabs = [
    {
      key: "overview",
      label: "Overview",
      content: (
        <OverviewTab hive={hive} actions={actions || []} photos={photos} />
      ),
    },
    {
      key: "queen",
      label: "Queen",
      content: (
        <LockedFeature enabled={hasFeature("QueenManagement")} featureLabel="Queen Management" previewText="Track lineage, marking color, and performance for this hive's queen.">
          <QueenTab hive={hive} onChanged={load} />
        </LockedFeature>
      ),
    },
    {
      key: "treatments",
      label: "Treatments",
      content: (
        <LockedFeature enabled={hasFeature("Treatments")} featureLabel="Treatments" previewText="Log mite/foulbrood/nosema treatments and track rotation.">
          <TreatmentsTab hive={hive} treatments={treatments || []} onLogged={load} />
        </LockedFeature>
      ),
    },
    {
      key: "feedings",
      label: "Feedings",
      content: (
        <LockedFeature enabled={hasFeature("Feedings")} featureLabel="Feedings" previewText="Track syrup, fondant, and pollen patty feedings with consumption rates.">
          <FeedingsTab hive={hive} feedings={feedings || []} onLogged={load} />
        </LockedFeature>
      ),
    },
    {
      key: "harvest",
      label: "Harvest",
      content: (
        <LockedFeature enabled={hasFeature("HarvestProducts")} featureLabel="Harvest & Products" previewText="Log honey, wax, propolis, and pollen harvests for this hive.">
          <HarvestTab hive={hive} />
        </LockedFeature>
      ),
    },
    {
      key: "equipment",
      label: "Equipment",
      content: (
        <LockedFeature enabled={hasFeature("EquipmentManagement")} featureLabel="Equipment Management" previewText="See boxes, frames, and feeders assigned to this hive.">
          <EquipmentTab hive={hive} />
        </LockedFeature>
      ),
    },
    {
      key: "bloom",
      label: "Bloom",
      content: (
        <LockedFeature enabled={hasFeature("BloomCalendar")} featureLabel="Bloom Calendar" previewText="See nearby forage plants and bloom windows for this hive's site.">
          <BloomTab hive={hive} />
        </LockedFeature>
      ),
    },
    {
      key: "colony",
      label: "Split/Swarm/Merge",
      content: (
        <LockedFeature enabled={hasFeature("ColonyManagement")} featureLabel="Colony Management" previewText="Log splits, swarm captures, and merges.">
          <ColonyEventsTab hive={hive} onLogged={load} onMerged={(id) => navigate(`/hives/${id}`)} />
        </LockedFeature>
      ),
    },
  ];

  return (
    <div>
      {/* Walk the row without backing out to the list (#267). Above the
          heading, because it is about where you ARE rather than about this
          hive, and because a keeper reaching for "next" should not have to
          scroll past a page of detail to find it. */}
      <HiveRowNav hive={hive} onNavigate={(id) => navigate(`/hives/${id}`)} />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "var(--space-3)" }}>
        <div>
          {/* Site name first: hive_number restarts at 1 per site, so the
              number alone identifies nothing once there are two sites. */}
          <h1>{hive.site_name ? `${hive.site_name} — Hive ${hive.hive_number}` : `Hive ${hive.hive_number}`}</h1>
          <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
            <Badge tone={hive.status === "Active" ? "success" : "neutral"}>{hive.status}</Badge>
            <span style={{ color: "var(--color-text-muted)" }}>
              Last checked: {hive.last_checked ? new Date(hive.last_checked).toLocaleDateString() : "Never"}
            </span>
            {/* When it is next due, not only when it was last seen (#207).
                Both values arrive computed from the API against the user's
                own inspection interval, so this cannot disagree with the
                dashboard column. */}
            <span style={{ color: hive.needs_attention ? "var(--color-warning)" : "var(--color-text-muted)" }}>
              {hive.needs_attention
                ? hive.last_checked
                  ? `Overdue — was due ${formatDateOnly(hive.next_check_due)}`
                  : "Never checked — due now"
                : `Next check: ${formatDateOnly(hive.next_check_due) || "—"}`}
            </span>
          </div>
          {hive.site_latitude == null && (
            <p style={{ color: "var(--color-warning)", margin: "var(--space-2) 0 0", fontSize: "var(--font-size-sm)" }}>
              This hive's site has no location, so it won't show on the map. Use Site Location to set one.
            </p>
          )}
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)" }}>
          {/* Reachable from the hive view because that is where the gap was
              noticed: you are looking at a hive, you know its site is in the
              wrong place (or nowhere), and there was no way to fix it. */}
          <Button variant="secondary" onClick={() => setSiteLocationOpen(true)}>Site Location</Button>
          <QrCodeLabel hive={{ hive_id: hive.hive_id, hive_number: hive.hive_number }} trigger={<Button variant="secondary">Print QR Label</Button>} />
          <PrintHiveReport hiveId={hive.hive_id} trigger={<Button variant="secondary">Print Full Report</Button>} />
          {/* Reported from the field: there was no way to retire a hive from
              this page at all. The only archive path was the batch action
              bar, which is gated on BatchOperations -- so on Basic it was
              not merely missing here, it was impossible anywhere.
              DELETE /hives/{id} is ungated and soft (is_archived = 1). */}
          {/* #389. Beside Archive because both answer "is this hive in
              service", and somebody deciding one is standing where the
              other is. An ARCHIVED hive gets neither: it is out of the
              rotation entirely, and offering to close its season would
              imply it was still in one. */}
          {/* THE DISAGREEMENT, beside the control that settles it (#400).
              Mirrors mobile: two people closing the same yard offline
              produce two claims, and the losing one is kept so the whole
              team sees the ambiguity rather than only whichever phone
              pushed second. Choosing is just closing or opening it. */}
          {seasonDisputed(hive) && (
            <p role="status" style={{
              width: "100%", margin: 0, padding: "var(--space-3)",
              border: "1px solid var(--color-warning)",
              borderRadius: "var(--radius-md)",
              background: "color-mix(in srgb, var(--color-warning) 12%, transparent)",
            }}>
              <strong>The status of this hive is ambiguous.</strong>{" "}
              It currently reads{" "}
              <strong>
                {hive.season_closed_until
                  ? `closed until ${formatDateOnly(hive.season_closed_until)}`
                  : "open"}
              </strong>
              . {hive.season_conflict_by_name || "Someone else"} recorded{" "}
              <strong>
                {hive.season_conflict_until
                  ? `closed until ${formatDateOnly(hive.season_conflict_until)}`
                  : "open"}
              </strong>
              {" "}offline. Set it below to settle it — including leaving it as it is.
            </p>
          )}
          {hive.status !== "Archived" && (
            hive.season_closed_until ? (
              <Button variant="secondary" disabled={savingSeason} onClick={() => setSeason(null)}>
                {savingSeason ? "Opening…" : "Open for the Season"}
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => setSeasonOpen(true)}>Close for the Season</Button>
            )
          )}
          {hive.status !== "Archived" ? (
            <>
              {/* BESIDE ARCHIVE, NOT INSTEAD OF IT (#432). These are two
                  different acts and the loss report is the one that can be
                  asked for: app.HiveLossReports feeds the regulatory Loss
                  report, which has been empty since V012 because nothing
                  could write it -- and an empty loss report reads as a
                  season with no losses.
                  Recording a loss deliberately does NOT archive the hive.
                  Somebody may be about to restock it. */}
              <Button variant="secondary" onClick={() => setLossOpen(true)}>Record Loss</Button>
              <Button variant="secondary" onClick={() => setArchiveOpen(true)}>Archive Hive</Button>
            </>
          ) : (
            /* The way back (#365). Offered HERE because this is where
               somebody looking at an archived hive already is -- they
               reached it through the status filter, and until now nothing
               on the page offered a return.
               Not behind a confirmation: archiving is the destructive-ish
               half and has one. Restoring puts a hive back in a list, and
               can be undone by archiving it again. */
            <Button variant="secondary" disabled={restoring} onClick={restore}>
              {restoring ? "Restoring…" : "Restore Hive"}
            </Button>
          )}
        </div>
      </div>

      {hasFeature("HealthScoring") && healthScore && (
        <Card
          title="Health Score"
          className="section-spacing"
          /* WHAT THE NUMBERS MEAN, ON DEMAND (#454). Reported once the
             breakdown finally rendered (#433): "Even I'm unsure what's
             determining Queen, Brood, and Population." If the owner
             cannot say, nobody can.

             A disclosure rather than always-on prose, for HelpDisclosure's
             own reason: a hive card is read constantly and an explanation
             nobody needs today is noise every other day. */
          actions={scoreHelp.button}
        >
          {scoreHelp.panel(
            <>
              <p style={{ marginTop: 0 }}>
                Six factors, weighted. Each is read from what you have
                recorded — so a factor you have never recorded falls back to
                an assumption, and those assumptions are worth knowing.
              </p>
              <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "var(--space-2) var(--space-3)", margin: 0 }}>
                <dt><strong>Mite (25%)</strong></dt>
                <dd style={{ margin: 0 }}>
                  Your latest mite count: 1 or fewer scores 100, then 80, 60,
                  40, 20, and 0 above twelve. The heaviest factor — and a hive
                  you have <em>never</em> counted is assumed clean at 100, so an
                  untested hive and a healthy one look identical here.
                </dd>
                <dt><strong>Queen (20%)</strong></dt>
                <dd style={{ margin: 0 }}>
                  The temperament score from the most recent performance log
                  for this hive's current queen, out of ten. No log yet means 70.
                </dd>
                <dt><strong>Brood (20%)</strong></dt>
                <dd style={{ margin: 0 }}>
                  The brood pattern score from that <em>same</em> log, out of
                  ten. Also 70 until the queen has been scored once — which is
                  why Queen and Brood often move together.
                </dd>
                <dt><strong>Population (20%)</strong></dt>
                <dd style={{ margin: 0 }}>
                  How full the hive is: frames against boxes, counting ten
                  frames to a box. Taken from the hive's own box and frame
                  numbers rather than from an inspection, so it only changes
                  when you change those.
                </dd>
                <dt><strong>Behaviour (10%)</strong></dt>
                <dd style={{ margin: 0 }}>
                  100, dropping to 50 if an issue was logged on this hive in
                  the last thirty days. It returns on its own once that
                  passes.
                </dd>
                <dt><strong>Weather (5%)</strong></dt>
                <dd style={{ margin: 0 }}>
                  Foraging conditions at the site, and only from a reading in
                  the last twenty-four hours — older than that is a different
                  week's weather. With no reading the factor is dropped and
                  the other five are scaled up to a full 100, so
                  <strong> “no reading” costs you nothing</strong>.
                </dd>
              </dl>
            </>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-4)" }}>
            <div style={{ fontSize: "2.5rem", fontWeight: 700, color: healthScore.overall_score < 60 ? "var(--color-danger)" : healthScore.overall_score < 80 ? "var(--color-warning)" : "var(--color-success)" }}>
              {healthScore.overall_score}
            </div>
            <div style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>
              <div>Queen: {healthScore.factors?.queen_score}</div>
              <div>Brood: {healthScore.factors?.brood_score}</div>
              <div>Mite: {healthScore.factors?.mite_score}</div>
              <div>Population: {healthScore.factors?.population_score}</div>
              <div>Behavior: {healthScore.factors?.behavior_score}</div>
              {/* WEATHER, WHICH USED TO BE A CONSTANT (#433).
                  The score was computed with a hardcoded weather factor of
                  80 on every hive since V022, and the app called the
                  procedure that does it. Switching to the weather-aware one
                  moves every score, so the component doing the moving has
                  to be on screen -- otherwise the number changes for a
                  reason nobody can see.

                  NO READING IS NOT A LOW SCORE. Absent weather rescales the
                  other five weights rather than scoring zero, so showing a
                  blank or a 0 here would misread as "bad weather" for an
                  account that has simply never recorded a location. The
                  distinction is only expressible because usp_ emits the key
                  as null instead of omitting it. */}
              <div>
                Weather:{" "}
                {healthScore.factors?.weather_factor == null
                  ? <em>no reading</em>
                  : healthScore.factors.weather_factor}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card title="Quick Actions" className="section-spacing">
        {/* GROUPED, so the screen does the triage (#411 follow-up).

            The overflow menu this replaces was rule 6 applied literally
            -- four on a surface, then a menu -- and it read as
            inconsistent: two frame actions on the row and two behind a
            button, with nothing about the pair saying which. Rule 6's
            REASON is that nobody should scan a wall of buttons. Groups do
            that; a menu hides half of them by order of addition.

            Same groups as the phone, from services/hiveActions, so the
            two cannot disagree about what belongs together. */}
        {ACTION_GROUPS.map((group) => (
          <div key={group.key} style={{ marginBottom: "var(--space-3)" }}>
            <div style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)",
                          marginBottom: "var(--space-1, 4px)" }}>
              {group.label}
            </div>
            <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
              {group.actions.map((a) => (
                <Button key={a.type} variant="secondary"
                        onClick={() => setPendingAction(a)}>{a.label}</Button>
              ))}
              {/* A note is about the hive rather than the box, and it is
                  the one entry here that opens a form rather than
                  logging. */}
              {group.key === "hive" && (
                <Button variant="secondary" onClick={() => setNoteModalOpen(true)}>Add Note</Button>
              )}
            </div>
          </div>
        ))}
      </Card>

      {/* Directly under Quick Actions, as the user asked: "a small section
          above or below the quick actions" (#228). A checklist is a
          per-hive activity, and before this the only way to reach one was
          a sidebar link that made you leave the hive you were looking at.
          Gated on HealthScoring, the same flag as the checklists page --
          the card hides itself when the routes would 403. */}
      {hasFeature("HealthScoring") && <HiveChecklistCard hiveId={hive.hive_id} />}

      {/* ABOVE the tabs, not inside Overview (reported 27 Aug).
          #251 already put this on Overview rather than a tab of its own,
          reasoning that what is currently WRONG with a hive is the first
          thing you want and a tab is somewhere you have to remember to
          look. That reasoning did not go far enough: Overview is itself
          a tab, so an open issue still sat below the fold, behind a
          click, under the photo gallery.
          It also carries "Log an issue", which is a thing you do standing
          at the hive -- so it earns its place high even when the list is
          empty. */}
      <IssuePanel hiveId={hive.hive_id} onChanged={load} />

      {followUps?.length > 0 && (
        <Card title="Reminders" className="section-spacing">
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {followUps.map((f) => (
              <li key={f.task_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-3)", padding: "var(--space-2) 0", borderBottom: "1px solid var(--color-border)" }}>
                <div>
                  <strong>{new Date(`${String(f.due_date).slice(0, 10)}T00:00:00`).toLocaleDateString()}</strong>
                  {f.description ? <div style={{ color: "var(--color-text-muted)" }}>{f.description}</div> : null}
                </div>
                <Button size="sm" variant="secondary" onClick={() => completeFollowUp(f.task_id)}>Done</Button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card>
        <Tabs tabs={tabs} />
      </Card>

      <LogActionDialog
        open={Boolean(pendingAction)}
        actionType={pendingAction?.type}
        actionLabel={pendingAction?.label}
        hive={hive}
        /* So the dialog can say "already logged today" (#375). The
           timeline is loaded here; the dialog would otherwise have to
           fetch its own copy and could disagree with the page behind it. */
        actions={actions}
        /* Honey that must not go in a jar yet (#404). Loaded here for
           the same reason `actions` is: the dialog fetching its own copy
           could disagree with the page behind it. */
        withdrawal={withdrawal}
        onCancel={() => setPendingAction(null)}
        onSubmit={submitAction}
      />

      <RecordLossDialog
        open={lossOpen}
        hive={hive}
        onClose={() => setLossOpen(false)}
        // The timeline gains a check, so the page behind it should not be
        // showing the older one.
        onRecorded={() => load()}
      />

      <SiteLocationDialog
        siteId={hive.site_id}
        siteName={hive.site_name}
        open={siteLocationOpen}
        onClose={() => setSiteLocationOpen(false)}
        // Reload so the header reflects a renamed site, and so the map
        // picks up coordinates that were just added.
        onSaved={load}
      />

      <Modal
        open={noteModalOpen}
        onClose={() => setNoteModalOpen(false)}
        title="Add Note"
        footer={<Button onClick={submitNote}>Save Note</Button>}
      >
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <textarea id="hive-detail-page-note-text" name="note-text"
            rows={4}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Type or use voice-to-text…"
            style={{ width: "100%" }}
            autoFocus
          />
          <VoiceNoteButton
            hiveId={hiveId}
            onResult={(text) => setNoteText((prev) => (prev ? `${prev} ${text}` : text))}
          />
        </div>
      </Modal>

      {/* A FORM, not a confirmation: it has a field, and the standard's
          first rule is that anything with a field is a form. Asking "are
          you sure?" and also taking a date is how the date ends up
          optional by accident. */}
      <Modal
        open={seasonOpen}
        onClose={() => setSeasonOpen(false)}
        title="Close this hive for the season"
        footer={
          // NOT "Close for the Season": that is the trigger behind this
          // dialog, and while the dialog is open both sit in the DOM with
          // the same accessible name. A screen reader hears the same words
          // twice with nothing to tell them apart -- and the tests could
          // not name one either, which is how it was noticed. Say what
          // THIS button does.
          <Button disabled={savingSeason || !closedUntil} onClick={() => setSeason(closedUntil)}>
            {savingSeason ? "Closing…" : "Close This Hive"}
          </Button>
        }
      >
        <p style={{ marginTop: 0 }}>
          While it is closed this hive is checked on your closed-season schedule
          instead of the ordinary one, so it stops being flagged as overdue every day.
        </p>
        {/* Closed is not silent, and saying so is the difference between
            a longer schedule and what somebody might assume is a mute. */}
        <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
          It is a longer schedule, not a pause — winter still has real checks, and the
          hive opens again by itself on the date you choose.
        </p>
        <label>
          Closed until
          <input id="hive-detail-page-closed-until" name="closed-until"
            type="date"
            /* No past dates: one would do nothing at all and read as a
               broken save. */
            min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
              .toISOString().slice(0, 10)}
            value={closedUntil}
            onChange={(e) => setClosedUntil(e.target.value)}
            className="touch-target"
          />
        </label>
      </Modal>

      <ConfirmDialog
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        onConfirm={archiveHive}
        title="Archive this hive?"
        confirmLabel={archiving ? "Archiving…" : "Archive Hive"}
      >
        {/* Say plainly that this is reversible. "Archive" reads as
            destructive to someone who has just lost a colony, and the
            record is exactly what they need to keep. */}
        <p>
          {hive.site_name ? `${hive.site_name} — Hive ${hive.hive_number}` : `Hive ${hive.hive_number}`} will
          be moved out of your active list.
        </p>
        <p>
          Its history is kept, and you can still find it by showing archived hives. Nothing is deleted.
        </p>
        {/* WHY, so the archived list is readable (#428).
            Asked for: "when you look at the hive archive you see a list of
            hives in the archived state and the reason WHY they were".
            Every archived hive used to carry the identical sentence.

            OPTIONAL, and it stays optional. Somebody archiving a hive has
            usually just lost a colony, and that is not the moment to
            insist on paperwork -- a required field here would be answered
            with whatever dismisses it fastest, which is worse than blank.

            SelectOrAdd rather than a text box: these reasons repeat across
            an apiary and across seasons, and a list of archived hives is
            only useful when they GROUP. The note beside it carries what
            never repeats. */}
        <SelectOrAdd
          listKey="archive_reason"
          label="Why? (optional)"
          value={archiveReason}
          onChange={setArchiveReason}
          placeholder="Not saying"
        />
        <Field label="Anything to add? (optional)">
          {(id, aria) => (
            <input id={id} {...aria} className="touch-target" value={archiveNote}
              maxLength={500}
              placeholder="Merged into Hive 14"
              onChange={(e) => setArchiveNote(e.target.value)} />
          )}
        </Field>
      </ConfirmDialog>
    </div>
  );
}
